"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Container,
  Card,
  CardBody,
  VStack,
  Heading,
  Text,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Button,
  Link as ChakraLink,
  Box,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import PasswordStrengthBar from "./PasswordStrengthBar";
import {
  validatePassword,
  mapSupabasePasswordError,
  PASSWORD_MIN_LENGTH,
} from "../lib/passwordPolicy";

export default function SetNewPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValidSession, setIsValidSession] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          console.error("Session error:", error);
          setIsValidSession(false);
          return;
        }

        if (data.session) {
          console.log("Valid session found");
          setIsValidSession(true);
        } else {
          console.log("No valid session found");
          setIsValidSession(false);
        }
      } catch (err) {
        console.error("Error checking session:", err);
        setIsValidSession(false);
      }
    };

    checkSession();
  }, []);

  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    const passwordCheck = validatePassword(newPassword);
    if (!passwordCheck.valid) {
      setError(passwordCheck.message || "Password does not meet requirements");
      setLoading(false);
      return;
    }

    try {
      console.log("Attempting to update password...");

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        console.error("Password update error:", error);
        throw error;
      }

      console.log("Password updated successfully");
      setSuccess(true);
      setLoading(false);

      // Show success message for 2 seconds before redirecting
      setTimeout(() => {
        console.log("Redirecting to login...");
        navigate("/login", {
          state: {
            message:
              "Password reset successful. Please sign in with your new password.",
          },
        });
      }, 2000);
    } catch (error: unknown) {
      console.error("Password reset error:", error);
      setError(mapSupabasePasswordError(error));
      setLoading(false);
      setSuccess(false);
    }
  };

  if (!isValidSession) {
    return (
      <Container maxW="md" py={12}>
        <Card>
          <CardBody p={8}>
            <VStack spacing={6} textAlign="center">
              <Heading size="lg" color="red.500">
                Invalid Link
              </Heading>
              <Text color="gray.600">
                This password reset link is invalid or has expired.
              </Text>
              <Button as={Link} to="/forgot-password" colorScheme="primary">
                Request New Link
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxW="md" py={12}>
      <Card>
        <CardBody p={8}>
          <VStack spacing={6}>
            <Box textAlign="center">
              <Box
                as="img"
                src="/main-logo.png"
                alt="CallSupplai Logo"
                h="60px"
                mx="auto"
                mb={2}
              />

              <Text color="secondary.600" fontSize="sm">
                Please enter your{" "}
                <Text as="span" color="purple.600">
                  new password
                </Text>{" "}
                below.
              </Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md" textColor={"red.600"}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            {success && (
              <Alert status="success" borderRadius="md" textColor={"green.600"}>
                <AlertIcon />
                Password reset successful! Redirecting to sign in...
              </Alert>
            )}

            <Box as="form" onSubmit={handlePasswordReset} w="100%">
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel color="gray.600">New Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a strong password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      minLength={PASSWORD_MIN_LENGTH}
                      bg="gray.50"
                      disabled={loading || success}
                      _placeholder={{ color: "gray.500" }}
                      textFillColor={"gray.600"}
                      sx={{ caretColor: "black" }}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        onClick={() => setShowPassword(!showPassword)}
                        variant="ghost"
                        size="sm"
                        disabled={loading || success}
                        color={"primary.500"}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <PasswordStrengthBar password={newPassword} />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="gray.600">Confirm New Password</FormLabel>
                  <InputGroup>
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      bg="gray.50"
                      disabled={loading || success}
                      _placeholder={{ color: "gray.500" }}
                      textFillColor={"gray.600"}
                      sx={{ caretColor: "black" }}
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                        icon={
                          showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />
                        }
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        variant="ghost"
                        size="sm"
                        disabled={loading || success}
                        color={"primary.500"}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  variant="brandGradient"
                  type="submit"
                  size="lg"
                  w="100%"
                  isLoading={loading}
                  loadingText="Resetting..."
                  disabled={success}
                >
                  {success ? "Password Reset!" : "Reset Password"}
                </Button>

                <ChakraLink as={Link} to="/login" color="primary.600">
                  Back to Sign In
                </ChakraLink>
              </VStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Container>
  );
}
